import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { resolveVariantTitle } from "./variant";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;
type StoreSellerCartsSummary = SalonStockerHttpTypes.StoreSellerCartsSummary;
type StoreLiveProvenance = SalonStockerHttpTypes.StoreLiveProvenance;

/**
 * KAN-105's all-live rule for a roll-up *across sellers*: `"live"` only if
 * every value that has a provenance at all was live, `"estimated"` if any
 * fell open, and omitted when none carries one.
 *
 * Absent values are skipped rather than counted against - across sellers the
 * field is missing precisely because the marker doesn't apply (an internal or
 * external supplier), so counting them would grey-mark every mixed basket
 * that merely contains a non-direct supplier. Within a single live seller's
 * cart the opposite holds; see {@link deriveVisibleTotalsProvenance}.
 *
 * Mirrors how the API's `seller-carts` route builds `summary.provenance` -
 * filter to the present values, then collapse. The two must agree: a buyer
 * who clears the cart's filters should not watch the marker change meaning.
 */
export function collapseProvenance(
  values: Array<StoreLiveProvenance | undefined>
): StoreLiveProvenance | undefined {
  const present = values.filter(
    (provenance): provenance is StoreLiveProvenance => !!provenance
  );

  if (present.length === 0) return undefined;

  return present.every((provenance) => provenance === "live")
    ? "live"
    : "estimated";
}

/**
 * A seller cart's item count, counting units rather than distinct lines - two
 * bottles of the same product are 2 items, not 1. Single owner for the
 * formula: {@link calculateSummary} and every seller-cart-card header read it
 * from here rather than re-deriving it, after one of them drifted to
 * `items.length` (line count, not unit count) and disagreed with the rest.
 */
export function sellerCartItemCount(cart: StoreSellerCart): number {
  return cart.items.reduce((sum, item) => sum + item.quantity, 0);
}

/**
 * How many ad hoc supplier lines (KAN-193) sit across the given carts - counted
 * as distinct lines, not units, since each is one uncomparable entry.
 *
 * Backs the Compare view's "not comparable" note: an ad hoc item has no
 * `product_id` and no other-supplier offers, so it can never be a row in the
 * cross-supplier price matrix. The note tells the salon those items exist and
 * where to find them, rather than letting them silently vanish from that view.
 */
export function countAdHocLines(carts: StoreSellerCart[]): number {
  return carts.reduce(
    (sum, cart) => sum + cart.items.filter((item) => item.ad_hoc).length,
    0
  );
}

/**
 * Calculates summary totals for a subset of seller carts.
 *
 * Used for:
 * - Filtered cart display (when user selects specific sellers)
 * - Multi-cart checkout (calculating totals for selected carts)
 *
 * The API returns its own `summary` alongside the carts, but it always
 * describes *every* cart - so once a supplier or category filter is on, or
 * checkout is working from a selected subset, the visible totals are these
 * and not the server's. `provenance` is folded here for the same reason and
 * by the same rule (see {@link collapseProvenance}), rather than passing the
 * server's field through to sit above numbers it doesn't describe.
 *
 * @param carts - Array of seller carts to summarize
 * @returns Aggregated summary with all totals
 *
 * @example
 * ```ts
 * const selectedCarts = allCarts.filter(c => selectedIds.includes(c.cart_id));
 * const summary = calculateSummary(selectedCarts);
 * ```
 */
export function calculateSummary(
  carts: StoreSellerCart[]
): StoreSellerCartsSummary {
  if (carts.length === 0) {
    return {
      total_items: 0,
      total_sellers: 0,
      item_subtotal: 0,
      shipping_total: 0,
      discount_total: 0,
      tax_total: 0,
      grand_total: 0
    };
  }

  const totals = carts.reduce(
    (acc, cart) => {
      return {
        total_items: acc.total_items + sellerCartItemCount(cart),
        total_sellers: acc.total_sellers + 1,
        item_subtotal: acc.item_subtotal + cart.totals.item_subtotal,
        shipping_total: acc.shipping_total + cart.totals.shipping_total,
        discount_total: acc.discount_total + cart.totals.discount_total,
        tax_total: acc.tax_total + cart.totals.tax_total,
        grand_total: acc.grand_total + cart.totals.total
      };
    },
    {
      total_items: 0,
      total_sellers: 0,
      item_subtotal: 0,
      shipping_total: 0,
      discount_total: 0,
      tax_total: 0,
      grand_total: 0
    }
  );

  const provenance = collapseProvenance(
    carts.map((cart) => cart.totals.provenance)
  );

  // Spread-when-present, not `provenance: undefined` - the field's absence is
  // the tri-state's third value, and an explicit `undefined` key would
  // survive a `JSON.stringify` round-trip as a missing key anyway but read as
  // a deliberate null to anything inspecting the object.
  return {
    ...totals,
    ...(provenance ? { provenance } : {})
  };
}

/**
 * Validates if a cart selection is valid for checkout.
 *
 * @param selectedCartIds - Array of selected cart IDs
 * @param allCarts - All available seller carts
 * @returns True if selection is valid for checkout
 *
 * @example
 * ```ts
 * const canCheckout = isValidCheckoutSelection(selectedIds, allCarts);
 * ```
 */
export function isValidCheckoutSelection(
  selectedCartIds: string[],
  allCarts: StoreSellerCart[]
): boolean {
  if (selectedCartIds.length === 0) {
    return false;
  }

  // Verify all selected cart IDs exist in available carts
  const allCartIds = new Set(allCarts.map((c) => c.cart_id));
  return selectedCartIds.every((id) => allCartIds.has(id));
}

// The placeholder-variant rule is app-wide (cart, order summary, product
// cards, product dialog), so it lives in its own module. Re-exported here
// because the cart line-item helpers below are its main consumers.
export { resolveVariantTitle };

/**
 * Matches a trailing " - Blonde 9.0" / "- 250ml" / "– 250ml" style suffix -
 * the separators Medusa and common import formats use between product and
 * variant in a combined line title.
 */
function stripVariantSuffix(title: string, variantTitle: string): string {
  const escaped = variantTitle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const suffix = new RegExp(`[\\s\\-–—,/|]+${escaped}\\s*$`, "i");
  return title.replace(suffix, "").trim();
}

/**
 * The product name to head a line item with.
 *
 * Medusa's line-item `title` names the *ordered thing*, so for a product with
 * real options it already carries the variant ("Professional Hair Color -
 * Blonde 9.0"). Printing that above a separate variant line states the variant
 * twice. `product_title` is the product on its own, which is what a heading
 * wants - but it is optional on the API type, so `title` stays the fallback.
 *
 * A genuine variant is stripped off the end of whichever string is used, not
 * just the fallback: suppliers' feeds vary, and `product_title` is only the
 * bare product when the supplier populated it that way. Recovering the bare
 * product from the combined string is the guarantee, whatever the source.
 *
 * Pairs with {@link resolveVariantTitle}: heading names the product, the line
 * beneath names the option, neither repeats the other.
 */
export function resolveProductTitle(item: {
  title: string;
  product_title?: string | null;
  variant_title?: string | null;
}): string {
  const base = item.product_title?.trim() || item.title;

  const variantTitle = resolveVariantTitle(item.variant_title);
  if (!variantTitle) return base;

  const stripped = stripVariantSuffix(base, variantTitle);
  return stripped || base;
}

/** Case/whitespace-insensitive equality for display strings. */
function sameText(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export interface LineItemDisplay {
  /** Heading: the product on its own. */
  productTitle: string;
  /** Brand/secondary line, or `null` when it would repeat another line. */
  subtitle: string | null;
  /** The chosen option, or `null` when placeholder or already stated. */
  variantTitle: string | null;
}

/**
 * The three text lines a cart line item renders, with every duplicate removed.
 *
 * A line item has up to four candidate strings (`product_title`, `title`,
 * `subtitle`, `variant_title`) and suppliers populate them inconsistently -
 * some put the variant in `title`, some repeat it in `subtitle`, some set
 * `subtitle` to the brand. Rather than assume a mapping, each line is
 * suppressed when it merely restates one already shown. `sellerName` is
 * passed so the brand line can drop when it equals the supplier that the
 * row already names.
 *
 * Single owner for the rule - the cart renders line items in the centre
 * column and the order summary, and the two disagreeing about the same
 * product is exactly the drift this prevents.
 */
export function resolveLineItemDisplay(
  item: {
    title: string;
    product_title?: string | null;
    subtitle?: string | null;
    variant_title?: string | null;
  },
  sellerName?: string
): LineItemDisplay {
  const productTitle = resolveProductTitle(item);
  const variant = resolveVariantTitle(item.variant_title);

  // Drop a variant that the heading already states outright.
  const variantTitle = sameText(variant, productTitle) ? null : variant;

  // Medusa mirrors the variant title into `subtitle`, so for a product with
  // no real options this line is the placeholder - the same noise the variant
  // line is filtered for, arriving through a different field.
  const rawSubtitle = resolveVariantTitle(item.subtitle);

  // Drop a brand line that restates the supplier, the heading, or the variant.
  const subtitle =
    sameText(rawSubtitle, sellerName) ||
    sameText(rawSubtitle, productTitle) ||
    sameText(rawSubtitle, variantTitle)
      ? null
      : rawSubtitle;

  return { productTitle, subtitle, variantTitle };
}

/** Categories per product id, as returned by `useProductCategories`. */
type CategoriesByProduct = Record<string, Array<{ id: string; name: string }>>;

export interface CartCategoryOption {
  id: string;
  name: string;
  /** Units across all carts that belong to this category. */
  itemCount: number;
}

/**
 * The distinct categories represented by the products in these carts, with a
 * unit count each - the option list for the cart's category filter. Sorted by
 * name so the list is stable between renders.
 */
export function collectCartCategories(
  carts: StoreSellerCart[],
  categoriesByProduct: CategoriesByProduct
): CartCategoryOption[] {
  const byId = new Map<string, CartCategoryOption>();

  for (const cart of carts) {
    for (const item of cart.items) {
      if (!item.product_id) continue;
      for (const category of categoriesByProduct[item.product_id] ?? []) {
        const existing = byId.get(category.id);
        if (existing) {
          existing.itemCount += item.quantity;
        } else {
          byId.set(category.id, {
            id: category.id,
            name: category.name,
            itemCount: item.quantity
          });
        }
      }
    }
  }

  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The per-cart all-live rule, applied to the lines actually on screen.
 *
 * Gated on the cart already carrying a `totals.provenance`, which is the
 * server's answer to "is this seller currently direct-integration-live" - the
 * one question the storefront can't ask for itself, since the cart payload
 * deliberately withholds a seller's checkout style. When that answer is "no
 * marker", it stays "no marker" however the view is filtered.
 *
 * Past that gate the rule is the API's: live only if every contributing value
 * was live, and a line missing a provenance counts against it (an absent
 * value inside a live seller's cart is a value nothing vouched for).
 *
 * Ad hoc supplier items (`item.ad_hoc`, KAN-193) are excluded from the
 * contributing list entirely, rather than counted as a missing-provenance
 * line against it. An ad hoc line is a hand-typed, fixed-price item that
 * never had a live call to begin with - it is fixed by design, not a value
 * "nothing vouched for" - so folding its absent `provenance` into this
 * roll-up would wrongly downgrade an otherwise all-live cart to "Estimated"
 * the moment one is present under a category filter. This holds regardless
 * of whether an ad hoc line also happens to be excluded upstream (its
 * `product_id` is null server-side, which already drops it out of
 * `visibleItems` via {@link filterCartsByCategories}'s own guard) - the rule
 * here must be correct on its own terms, not only as a side effect of that.
 */
function deriveVisibleTotalsProvenance(
  cart: StoreSellerCart,
  visibleItems: StoreSellerCart["items"]
): StoreLiveProvenance | undefined {
  if (!cart.totals.provenance) return undefined;

  const contributing = [
    ...visibleItems
      .filter((item) => !item.ad_hoc)
      .map((item) => item.provenance),
    // `?? []` for the same reason `OrderSummary` guards it: the field is
    // required on the type but a cart with no method chosen yet is a real
    // state. Note `[].every()` is `true`, so this list must never be the only
    // contributor - it isn't, because the caller only reaches here for a cart
    // with at least one visible item.
    ...(cart.shipping_methods ?? []).map((method) => method.provenance)
  ];

  return contributing.every((provenance) => provenance === "live")
    ? "live"
    : "estimated";
}

/**
 * Narrows each cart to the line items in the selected categories, dropping
 * carts left with nothing.
 *
 * Unlike the supplier filter - which selects whole carts, so the server's
 * totals still apply - this cuts across carts, so `totals` must be restated
 * for the items that remain. `item_subtotal` is summed exactly from the
 * visible line items; `discount_total` and `tax_total` are **prorated** by the
 * visible share of the subtotal, because the API reports them per cart with no
 * per-item allocation to work from. They are therefore estimates whenever a
 * category filter is active. Shipping is left whole: it is charged per
 * delivery, not per item, so filtering the view doesn't reduce it.
 *
 * `totals.provenance` is restated for the same reason the money is: the
 * server's value collapses every line in the cart, so a filter that hides the
 * one line whose live price fell open would otherwise leave an "Estimated"
 * marker over a total that is now, visibly, all-live.
 */
export function filterCartsByCategories(
  carts: StoreSellerCart[],
  selectedCategoryIds: string[],
  categoriesByProduct: CategoriesByProduct
): StoreSellerCart[] {
  if (selectedCategoryIds.length === 0) return carts;

  const selected = new Set(selectedCategoryIds);
  const result: StoreSellerCart[] = [];

  for (const cart of carts) {
    const items = cart.items.filter((item) => {
      // Ad hoc supplier items (KAN-193) are not catalogue-linked - they carry
      // no product_id and no category, so a category filter has nothing to
      // match them on. They must still ALWAYS show (in their own "Additional
      // items" section), never be dropped: a category filter runs by default
      // (categories start all-selected), so treating a missing product_id as
      // "excluded" hides the rep's line from the cart while it still counts in
      // the header total - and drops the whole card when it is the cart's only
      // line. This exemption must come before the product_id guard below, which
      // exists to drop genuinely uncategorisable catalogue lines.
      if (item.ad_hoc) return true;
      if (!item.product_id) return false;
      return (categoriesByProduct[item.product_id] ?? []).some((category) =>
        selected.has(category.id)
      );
    });

    if (items.length === 0) continue;

    const visibleSubtotal = items.reduce(
      (sum, item) => sum + (item.subtotal ?? 0),
      0
    );
    const share =
      cart.totals.item_subtotal > 0
        ? visibleSubtotal / cart.totals.item_subtotal
        : 0;
    const discountTotal = cart.totals.discount_total * share;
    const taxTotal = cart.totals.tax_total * share;
    const visibleProvenance = deriveVisibleTotalsProvenance(cart, items);

    result.push({
      ...cart,
      items,
      totals: {
        ...cart.totals,
        item_subtotal: visibleSubtotal,
        discount_total: discountTotal,
        tax_total: taxTotal,
        total:
          visibleSubtotal +
          cart.totals.shipping_total +
          taxTotal -
          discountTotal,
        ...(visibleProvenance ? { provenance: visibleProvenance } : {})
      }
    });
  }

  return result;
}
